import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useUser } from "@/features/auth/hooks/use-user";
import { brandDocFor, documentedVendors } from "@/features/devices/brand-docs";
import {
  NEW_BASE,
  useCreateDeviceBase,
  useDeviceBasePrompt,
  useDeviceBaseHistory,
  useDeviceBases,
  useUpdateDeviceBase,
} from "@/features/devices/hooks/use-devices";
import type {
  DeviceBase,
  DeviceBaseHistoryEntry,
  DeviceBaseInput,
} from "@/features/devices/types";
import { useClientId } from "@/features/utils/api-scope";
import { useDateFnsLocale, useTranslation } from "@/features/utils/i18n";
import { Copy, Eye, EyeSlash, Info } from "@phosphor-icons/react";
import { format, formatDistanceToNow } from "date-fns";
import { useEffect, useState } from "react";
import { toast } from "sonner";

/** The fields, in the order they are read off the box and off the screen. */
const FIELDS = [
  "label",
  "brand",
  "base_pin",
  "web_username",
  "web_password",
  "notes",
] as const;

type Field = (typeof FIELDS)[number];

const formOf = (base?: DeviceBase): Required<DeviceBaseInput> => ({
  label: base?.label || "",
  brand: base?.brand || "",
  base_pin: base?.base_pin || "",
  web_username: base?.web_username || "",
  web_password: base?.web_password || "",
  notes: base?.notes || "",
});

/**
 * A secret with a reveal and a copy.
 *
 * Masked by default rather than as a security measure — it is readable by
 * anyone who clicks — but because this dialog is opened in front of a client,
 * on a screen someone else can be looking at, far more often than a password is
 * actually needed.
 */
const SecretInput = ({
  id,
  value,
  onChange,
  copiedLabel,
}: {
  id: string;
  value: string;
  onChange: (value: string) => void;
  copiedLabel: string;
}) => {
  const [visible, setVisible] = useState(false);

  const copy = async () => {
    await navigator.clipboard.writeText(value);
    toast.success(copiedLabel);
  };

  return (
    <div className="flex items-center gap-2">
      <Input
        id={id}
        // Not `type="password"`: the browser offers to save a credential it
        // recognises, and what it would save is a password for *our* domain,
        // under the owner's account, for a phone sitting in another building.
        className={visible ? "" : "[-webkit-text-security:disc]"}
        autoComplete="off"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
      <Button
        type="button"
        variant="ghost"
        size="icon"
        onClick={() => setVisible((shown) => !shown)}
      >
        {visible ? (
          <EyeSlash className="h-4 w-4" />
        ) : (
          <Eye className="h-4 w-4" />
        )}
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        onClick={copy}
        disabled={!value}
      >
        <Copy className="h-4 w-4" />
      </Button>
    </div>
  );
};

/**
 * What we know about this make of hardware, from the brand that was typed.
 *
 * Appears as soon as the brand matches a vendor we have written up, which is
 * also the point at which it is useful: someone is holding the box. The values
 * are factory defaults, so a base whose codes still match them is a finding —
 * showing the two side by side is how that gets noticed.
 */
const BrandDocs = ({ brand }: { brand: string }) => {
  const { t } = useTranslation("devices");
  const doc = brandDocFor(brand);

  if (!doc) return null;

  return (
    <div className="bg-muted/40 space-y-2 rounded-md border px-3 py-2">
      <div className="flex items-center gap-2">
        <Info className="h-4 w-4 shrink-0" />
        <span className="text-sm font-medium">
          {t("devices.bases.brandDocs.title", { vendor: doc.vendor })}
        </span>
      </div>
      <dl className="space-y-1">
        {doc.items.map((item) => (
          <div key={item.key} className="flex flex-wrap gap-x-2 text-xs">
            <dt className="text-muted-foreground">
              {t(`devices.bases.brandDocs.items.${item.key}`)}
            </dt>
            <dd className="font-mono">
              {/* A few values are a path through the device's own menus and
                  read in the user's language; the rest are literal settings. */}
              {t(
                `devices.bases.brandDocs.values.${doc.vendor.toLowerCase()}.${item.key}`,
                item.value,
              )}
            </dd>
          </div>
        ))}
      </dl>
      <p className="text-muted-foreground text-xs">
        {t("devices.bases.brandDocs.disclaimer")}
      </p>
    </div>
  );
};

/**
 * What this write changed, rather than the values it left behind.
 *
 * The audit table stores a full snapshot per write, so rendering it as-is gives
 * six identical lines per entry and buries the one field that moved. Entries
 * arrive newest first, so the state before entry `i` is entry `i + 1`; the
 * oldest has no predecessor and every non-empty field counts as set.
 */
const changesOf = (
  entry: DeviceBaseHistoryEntry,
  previous?: DeviceBaseHistoryEntry,
): { field: Field; from: string; to: string }[] =>
  FIELDS.map((field) => ({
    field,
    from: previous?.[field] || "",
    to: entry[field] || "",
  })).filter((change) => change.from !== change.to);

/**
 * The audit trail, for platform staff only.
 *
 * The route is SYSADMIN-only and answers 403 to the tenant's own owner, so this
 * is rendered — and fetched — only for staff. It exists for support: "the PIN
 * worked last month" is answered by what changed since, and by whom.
 */
const BaseHistory = ({ baseId }: { baseId: string }) => {
  const { t } = useTranslation("devices");
  const clientId = useClientId();
  const locale = useDateFnsLocale();

  const { data: history = [], isLoading } = useDeviceBaseHistory(
    clientId || "",
    baseId,
  );

  return (
    <div className="space-y-3 border-t pt-4">
      <div className="space-y-1">
        <h4 className="text-sm font-medium">
          {t("devices.bases.history.title")}
        </h4>
        <p className="text-muted-foreground text-xs">
          {t("devices.bases.history.description")}
        </p>
      </div>

      {isLoading && (
        <p className="text-muted-foreground text-sm">
          {t("devices.bases.history.loading")}
        </p>
      )}

      {!isLoading && history.length === 0 && (
        <p className="text-muted-foreground text-sm">
          {t("devices.bases.history.empty")}
        </p>
      )}

      <div className="space-y-2">
        {history.map((entry, index) => {
          const changes = changesOf(entry, history[index + 1]);
          const at = new Date(entry.operation_timestamp);

          return (
            <div
              key={`${entry.operation_timestamp}-${index}`}
              className="space-y-2 rounded-md border px-3 py-2"
            >
              <div className="flex flex-wrap items-center gap-2">
                <Badge
                  variant={
                    entry.operation === "DELETE" ? "destructive" : "secondary"
                  }
                >
                  {t(`devices.bases.history.operations.${entry.operation}`)}
                </Badge>
                <span className="text-xs">
                  {format(at, "PPpp", { locale })}
                </span>
                <span className="text-muted-foreground text-xs">
                  {formatDistanceToNow(at, { addSuffix: true, locale })}
                </span>
                <span className="text-muted-foreground text-xs">
                  ·{" "}
                  {entry.author
                    ? entry.author.full_name || entry.author.email
                    : t("devices.bases.history.unknownAuthor")}
                </span>
              </div>

              {changes.length === 0 ? (
                <p className="text-muted-foreground text-xs">
                  {t("devices.bases.history.noChange")}
                </p>
              ) : (
                <ul className="space-y-1">
                  {changes.map((change) => (
                    <li key={change.field} className="text-xs">
                      <span className="text-muted-foreground">
                        {t(`devices.bases.fields.${change.field}`)}
                      </span>{" "}
                      <span className="font-mono line-through opacity-60">
                        {change.from || t("devices.bases.history.blank")}
                      </span>{" "}
                      <span className="text-muted-foreground">→</span>{" "}
                      <span className="font-mono">
                        {change.to || t("devices.bases.history.blank")}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

/**
 * One base: what it is, the codes to get into it, and the handsets on it.
 *
 * Mounted by the page rather than by the row that opens it — the lists refetch
 * on an interval, and a dialog owned by a table cell is dismissed mid-edit when
 * React reconciles that row.
 *
 * `baseId === NEW_BASE` is the creation form: same fields, same dialog, because
 * "what do I fill in" has the same answer either way.
 */
export const DeviceBaseDialog = ({
  baseId,
  onClose,
}: {
  baseId: string | null;
  onClose: () => void;
}) => {
  const { t } = useTranslation("devices");
  const clientId = useClientId();
  const { data: user } = useUser();

  const { byId, isLoading } = useDeviceBases(clientId || "");
  const create = useCreateDeviceBase();
  const update = useUpdateDeviceBase();

  const creating = baseId === NEW_BASE;
  const base = baseId && !creating ? byId.get(baseId) : undefined;

  const [form, setForm] = useState<Required<DeviceBaseInput>>(formOf());

  // Reset from the stored base whenever a different one is opened — and once
  // more when the list finishes loading, in case the dialog was opened before
  // the query settled.
  useEffect(() => {
    if (baseId) setForm(formOf(creating ? undefined : base));
    // Deliberately keyed on the id and on the row's *stamp*, not on the row
    // object: that one is rebuilt by every refetch, and depending on it would
    // wipe what is being typed a few seconds in.
  }, [baseId, creating, base?.id, base?.updated_at]);

  const set = (field: Field, value: string) =>
    setForm((current) => ({ ...current, [field]: value }));

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!clientId || !baseId) return;
    try {
      if (creating) await create.mutateAsync({ clientId, data: form });
      else await update.mutateAsync({ clientId, baseId, data: form });
      toast.success(t("devices.bases.saved"));
      onClose();
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : t("devices.bases.saveFailed"),
      );
    }
  };

  const pending = create.isPending || update.isPending;

  return (
    <Dialog open={!!baseId} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl">
        <form onSubmit={handleSave}>
          <DialogHeader>
            <DialogTitle>
              {t(creating ? "devices.bases.createTitle" : "devices.bases.title")}
            </DialogTitle>
            <DialogDescription>
              {t("devices.bases.description")}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="base-label">
                {t("devices.bases.fields.label")}
              </Label>
              <Input
                id="base-label"
                value={form.label}
                placeholder={t("devices.bases.placeholders.label")}
                onChange={(e) => set("label", e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="base-brand">
                {t("devices.bases.fields.brand")}
              </Label>
              <Input
                id="base-brand"
                value={form.brand}
                list="base-brand-vendors"
                placeholder={t("devices.bases.placeholders.brand")}
                onChange={(e) => set("brand", e.target.value)}
              />
              {/*
                The vendors we have notes for, offered rather than imposed: the
                field is free text because the model matters and we cannot know
                every box a client owns.
              */}
              <datalist id="base-brand-vendors">
                {documentedVendors().map((vendor) => (
                  <option key={vendor} value={vendor} />
                ))}
              </datalist>
            </div>

            <BrandDocs brand={form.brand} />

            <div className="space-y-2">
              <Label htmlFor="base-pin">
                {t("devices.bases.fields.base_pin")}
              </Label>
              <SecretInput
                id="base-pin"
                value={form.base_pin}
                onChange={(value) => set("base_pin", value)}
                copiedLabel={t("devices.bases.copied")}
              />
              <p className="text-muted-foreground text-xs">
                {t("devices.bases.help.base_pin")}
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="base-web-username">
                {t("devices.bases.fields.web_username")}
              </Label>
              <Input
                id="base-web-username"
                value={form.web_username}
                autoComplete="off"
                onChange={(e) => set("web_username", e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="base-web-password">
                {t("devices.bases.fields.web_password")}
              </Label>
              <SecretInput
                id="base-web-password"
                value={form.web_password}
                onChange={(value) => set("web_password", value)}
                copiedLabel={t("devices.bases.copied")}
              />
              <p className="text-muted-foreground text-xs">
                {t("devices.bases.help.web_password")}
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="base-notes">
                {t("devices.bases.fields.notes")}
              </Label>
              <Textarea
                id="base-notes"
                rows={4}
                value={form.notes}
                placeholder={t("devices.bases.placeholders.notes")}
                onChange={(e) => set("notes", e.target.value)}
              />
            </div>

            {/*
              Which SIP accounts are on this box. Read-only here: a handset is
              attached from the handset's own form, which is where somebody is
              when they know the answer.
            */}
            {base && (
              <div className="space-y-2 border-t pt-4">
                <h4 className="text-sm font-medium">
                  {t("devices.bases.attached.title", {
                    count: base.devices.length,
                  })}
                </h4>
                {base.devices.length === 0 ? (
                  <p className="text-muted-foreground text-xs">
                    {t("devices.bases.attached.empty")}
                  </p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {base.devices.map((device) => (
                      <Badge key={device.id} variant="secondary">
                        {device.label}
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/*
              Staff only, and not merely hidden: the route behind it is
              SYSADMIN-only, so rendering it for an owner would fetch a 403.
            */}
            {base && user?.role === "SYSADMIN" && (
              <BaseHistory baseId={base.id!} />
            )}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              {t("devices.actions.cancel")}
            </Button>
            <Button type="submit" disabled={pending || isLoading}>
              {t("devices.actions.save")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

/**
 * The base dialog, wired to the prompt that says which base is open.
 *
 * Mounted **once per page**, not once per table: the handset list opens a base
 * from its action menu and the bases table opens one from its rows, and two
 * mounts bound to the same global prompt would render the dialog twice. Pages
 * that show either table mount this.
 */
export const DeviceBaseDialogHost = () => {
  const { baseId, clear } = useDeviceBasePrompt();
  return <DeviceBaseDialog baseId={baseId} onClose={clear} />;
};
